// src/Components/videochat/useCallWallet.js
//
// Saldo de coins siempre a la vista en la llamada y lo recibido en regalos.
//  - Al abrir se usa el saldo guardado y enseguida se pide GET /coins.
//  - Se vuelve a pedir cada vez que llega o se envía un regalo.
//  - Cuando el saldo SUBE: vibración, «+N» dorado y la cápsula crece al 112 %
//    unos instantes; el «+N» se desvanece a los 3 s.
//  - «+N en esta llamada»: coins de los regalos que me hicieron desde que
//    empezó la llamada (con 30 s de margen por el desfase de relojes).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useAtom } from 'jotai'
import config from '../../Config'
import { coinsAtom } from '../../Services/store/atoms'
import { giftCoins, isGiftMessage } from './callChatUtils'

const CLOCK_SKEW_MS = 30000
const DELTA_VISIBLE_MS = 3000
const PULSE_MS = 350

export default function useCallWallet({ authToken, messages, myId, callStartedAt }) {
  const [coins, setCoins] = useAtom(coinsAtom)
  const [loaded, setLoaded] = useState(false)
  const [delta, setDelta] = useState(0)
  const [pulse, setPulse] = useState(false)
  const prevRef = useRef(null)
  const timersRef = useRef([])

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${config.REACT_APP_BASE_URL}/api/v1/coins`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      const value = Number(res.data?.coins ?? res.data)
      if (Number.isFinite(value)) { setCoins(value); setLoaded(true) }
    } catch {
      // Se queda con el saldo guardado.
    }
  }, [authToken, setCoins])

  useEffect(() => { refresh() }, [refresh])

  // Vuelve a pedirlo cuando cambia el último mensaje de regalo del chat.
  const lastGiftId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) if (isGiftMessage(messages[i])) return messages[i].id
    return null
  }, [messages])
  useEffect(() => { if (lastGiftId != null) refresh() }, [lastGiftId, refresh])

  // Animación cuando sube.
  useEffect(() => {
    if (!loaded) return
    const prev = prevRef.current
    prevRef.current = coins
    if (prev == null || coins <= prev) return
    try { navigator.vibrate?.([20, 40, 20]) } catch {}
    setDelta(coins - prev)
    setPulse(true)
    timersRef.current.push(setTimeout(() => setPulse(false), PULSE_MS))
    timersRef.current.push(setTimeout(() => setDelta(0), DELTA_VISIBLE_MS))
  }, [coins, loaded])

  useEffect(() => () => timersRef.current.forEach(clearTimeout), [])

  const receivedInCall = useMemo(() => {
    const since = callStartedAt - CLOCK_SKEW_MS
    return messages.reduce((sum, m) => {
      if (String(m.sender_id) === String(myId) || !isGiftMessage(m)) return sum
      if (new Date(m.created_at).getTime() < since) return sum
      return sum + giftCoins(m)
    }, 0)
  }, [messages, myId, callStartedAt])

  return { coins: loaded ? coins : null, delta, pulse, receivedInCall, refresh }
}
